import { useLazyQuery } from '@apollo/client'
import { GET_USER_INFO } from '@/src/common/gql/queries/user'
import { useCallback, useEffect, useState } from 'react'
import { useUserStore } from '@/src/common/store/useUserStore'
import { useLogError } from '@/src/common/hooks/useLogError'
import { tokenStoreLoadingPromise, useTokenStore } from '@/src/common/store/useTokenStore'
import { useAppState } from '@/src/common/hooks/useAppState'
import type { GetUserInfoQuery } from '@api/mesa/graphql'
import { create } from 'zustand'

/**
 * this needs to be a getter pointing to the latest user info query.
 * if it just resolves to whatever the user info query is at the time of the promise's resolution,
 * the resolved value will not only be outdated but also a memory leak
 */
type UserInfoGetter = () => GetUserInfoQuery | null

const { promise, resolve } = Promise.withResolvers<UserInfoGetter>()

/**
 * `await` this promise to get the user info determined _after_ the token store has already loaded and the user info was _attempted_ to be fetched.
 * in other words, the user info you get by `await`ing this promise will be defined/undefined because the user actually exists/doesn't exist, NOT
 * because the user hasn't been authenticated or the app hasn't finished initializing (i.e. fetched the user info for the first time) yet
 */
export const getUserInfoPromise = promise

const initialized = create(() => false)

/** @todo clean this hook up to use `useQuery` instead of `useLazyQuery` */
export function useGetUserInformation() {
  /** @todo delete this in favor of solely using apollo's state */
  const [loading, setLoading] = useState(true)

  const { isAppVisible } = useAppState()
  const accessToken = useTokenStore().tokens?.accessToken
  const [getUserInfo, { loading: userInfoLoading, error, client }] = useLazyQuery(GET_USER_INFO, {
    fetchPolicy: 'network-only',
  })
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  const { setUserInformation, userInformation } = useUserStore()
  const logError = useLogError()

  const refreshUserInformation = useCallback(async () => {
    // make sure the absence/presence of `accessToken` is determined by the user's actual logged in/out state,
    // NOT by the fact that the token store hasn't finished loading from the AsyncStorage yet
    await tokenStoreLoadingPromise

    try {
      if (accessToken) {
        const request = await getUserInfo()
        resolve(() => client.cache.readQuery({ query: GET_USER_INFO }))
        const userData = request.data?.getUserInfo

        if (userData) {
          setUserInformation(userData)
        }

        setLoading(false)
        return userData
      }

      resolve(() => client.cache.readQuery({ query: GET_USER_INFO }))
      setLoading(false)
    } catch (error) {
      setLoading(false)
      logError(error)
      resolve(() => client.cache.readQuery({ query: GET_USER_INFO }))
    } finally {
      initialized.setState(true)
    }
  }, [accessToken, logError, getUserInfo, setUserInformation, client])

  useEffect(() => {
    if (isAppVisible) {
      void refreshUserInformation()
    }
  }, [refreshUserInformation, isAppVisible])

  return {
    user: userInformation,
    loading: loading || userInfoLoading,
    error,
    refreshUserInformation,
    initialized: initialized(),
  } as const
}
